import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  LocalShipping as LocalShippingIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

const drawerWidth = 240;

interface NavigationProps {
  open: boolean;
  onClose: () => void;
}

const Navigation: React.FC<NavigationProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const menuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
    },
    {
      text: 'Inventory Items',
      icon: <InventoryIcon />,
      path: '/inventory-items',
    },
    {
      text: 'Vendors',
      icon: <LocalShippingIcon />,
      path: '/vendors',
    },
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
    // Don't close on desktop, only on mobile
  };

  const renderMenuList = (isMobile: boolean) => (
    <List sx={{ pt: 1 }}>
      {menuItems.map((item) => (
        <ListItem key={item.text} disablePadding>
          <ListItemButton
            selected={location.pathname === item.path}
            onClick={() => {
              handleNavigation(item.path);
              if (isMobile) {
                onClose();
              }
            }}
            sx={{
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                '& .MuiListItemIcon-root': {
                  color: 'primary.contrastText',
                },
              },
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );

  return (
    <>
      {/* Desktop Navigation - Always visible, no title */}
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          position: 'fixed',
          top: '64px',
          left: 0,
          width: drawerWidth,
          height: 'calc(100vh - 64px)',
          backgroundColor: 'background.paper',
          borderRight: 1,
          borderColor: 'divider',
          zIndex: theme.zIndex.drawer,
          overflow: 'auto',
        }}
      >
        {renderMenuList(false)}
      </Box>

      {/* Mobile Navigation - Temporary drawer with title */}
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        sx={{
          display: { xs: 'block', md: 'none' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
            Inventory Management
          </Typography>
        </Box>
        <Divider />
        {renderMenuList(true)}
      </Drawer>
    </>
  );
};

export default Navigation;
